import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Typography,
  Divider,
  useTheme,
  alpha,
  ListItemIcon,
  ListItemText,
  Chip
} from '@mui/material';
import {
  CalendarToday,
  History,
  Schedule,
  DateRange,
  AllInclusive,
  ArrowDropDown,
  Check,
  Today,
  Event
} from '@mui/icons-material';
import CustomDateRangePicker from './CustomDateRangePicker';

interface DateFilterDropdownProps {
  value: string;
  onChange: (value: string) => void;
  showAllOption?: boolean;
  className?: string;
}

const DateFilterDropdown: React.FC<DateFilterDropdownProps> = ({
  value = 'recent',
  onChange,
  showAllOption = false,
  className = ''
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  const options = [
    {
      value: 'recent',
      label: 'Recent',
      sublabel: 'Today & Yesterday',
      emoji: true,
      color: '#FF6B6B' /* basil-todo */
    },
    {
      value: 'today',
      label: 'Today',
      sublabel: 'Current day only',
      emoji: true,
      color: '#4ECDC4' /* basil-todo */
    },
    {
      value: 'yesterday',
      label: 'Yesterday',
      sublabel: 'Previous day',
      emoji: true,
      color: '#95A5A6' /* basil-todo */
    },
    {
      value: 'week',
      label: 'This Week',
      sublabel: 'Last 7 days',
      emoji: true,
      color: '#3498DB' /* basil-todo */
    },
    {
      value: 'month',
      label: 'This Month',
      sublabel: 'Last 30 days',
      emoji: true,
      color: '#9B59B6' /* basil-todo */
    },
    ...(showAllOption ? [{
      value: 'all',
      label: 'All Time',
      sublabel: 'Complete history',
      emoji: true,
      color: '#F39C12' /* basil-todo */
    }] : []),
    {
      value: 'custom',
      label: 'Custom Range',
      sublabel: 'Pick specific dates',
      emoji: true,
      color: 'var(--status-bad)'
    }
  ];

  // Check if value is a custom date range (format: YYYY-MM-DD,YYYY-MM-DD)
  const isCustomRange = value.includes(',');
  const selectedOption = isCustomRange
    ? options.find(opt => opt.value === 'custom')!
    : (options.find(opt => opt.value === value) || options[0]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (newValue: string) => {
    if (newValue === 'custom') {
      // Extract dates if already a custom range
      if (isCustomRange) {
        const [start, end] = value.split(',');
        setCustomStartDate(start);
        setCustomEndDate(end);
      }
      setShowCustomPicker(true);
      handleClose();
    } else {
      onChange(newValue);
      handleClose();
    }
  };

  const handleCustomDateApply = (startDate: string, endDate: string) => {
    setCustomStartDate(startDate);
    setCustomEndDate(endDate);
    // Send date range in format: "YYYY-MM-DD,YYYY-MM-DD"
    onChange(`${startDate},${endDate}`);
    setShowCustomPicker(false);
  };

  return (
    <>
      <Button
        onClick={handleClick}
        variant="text"
        size="small"
        endIcon={<ArrowDropDown sx={{ fontSize: { xs: '0.9rem', sm: '1rem' }, color: 'var(--text-tertiary)' }} />}
        sx={{
          borderRadius: '6px',
          textTransform: 'none',
          backgroundColor: 'var(--bg-surface)',
          color: 'var(--text-primary)',
          border: '1px solid var(--border-default)',
          boxShadow: 'none',
          minWidth: { xs: 80, sm: 100 },
          height: { xs: 28, sm: 32 },
          fontSize: { xs: '0.7rem', sm: '0.8rem' },
          fontWeight: 600,
          padding: { xs: '2px 6px', sm: '4px 10px' },
          '&:hover': {
            backgroundColor: '#F8F8F8', /* basil-todo */
            borderColor: '#B0B0B0', /* basil-todo */
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          },
          '&.MuiButton-root': {
            backgroundColor: 'var(--bg-surface) !important',
            border: '1px solid var(--border-default) !important',
          },
          '& .MuiButton-startIcon': {
            marginRight: { xs: 0.2, sm: 0.3 },
            marginLeft: 0,
          },
          '& .MuiButton-endIcon': {
            marginLeft: { xs: 0.1, sm: 0.2 },
            marginRight: { xs: -0.2, sm: -0.3 },
          },
          transition: 'all 0.15s ease',
        }}
        className={className}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.3, sm: 0.5 } }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: 'var(--text-primary)',
              fontSize: { xs: '0.7rem', sm: '0.8rem' },
              lineHeight: 1
            }}
          >
            {selectedOption.label}
          </Typography>
        </Box>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 0.5,
            minWidth: { xs: 140, sm: 160 },
            maxWidth: { xs: 200, sm: 220 },
            borderRadius: '8px',
            border: '1px solid var(--border-strong)',
            backgroundColor: 'var(--bg-surface)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
            overflow: 'visible',
            '& .MuiMenuItem-root': {
              borderRadius: '6px',
              mx: 0.5,
              my: 0.15,
              fontSize: { xs: '0.75rem', sm: '0.85rem' },
              padding: { xs: '6px 10px', sm: '8px 12px' },
              minHeight: 'unset',
              transition: 'all 0.15s ease',
              '&:hover': {
                backgroundColor: 'var(--bg-sunken)',
                transform: 'translateX(2px)',
              },
              '&.Mui-selected': {
                backgroundColor: '#EBEBEB', /* basil-todo */
                '&:hover': {
                  backgroundColor: 'var(--bg-sunken)',
                }
              }
            },
            '& .MuiList-root': {
              padding: { xs: '4px', sm: '6px' },
            }
          }
        }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      >

        
        {options.map((option) => (
          <MenuItem
            key={option.value}
            selected={value === option.value}
            onClick={() => handleSelect(option.value)}
            sx={{
              py: { xs: 0.5, sm: 0.6 },
              px: { xs: 1, sm: 1.2 },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: { xs: 0.6, sm: 0.8 } }}>
              <span style={{
                fontSize: '1rem',
                display: 'inline-flex',
                alignItems: 'center',
                minWidth: '20px'
              }}>
              </span>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: value === option.value ? 600 : 500,
                  color: 'var(--text-primary)',
                  fontSize: { xs: '0.75rem', sm: '0.85rem' },
                  flexGrow: 1
                }}
              >
                {option.label}
              </Typography>
              {value === option.value && (
                <Check
                  fontSize="small"
                  sx={{
                    color: 'var(--status-ok)',
                    fontSize: { xs: '0.85rem', sm: '0.95rem' }
                  }}
                />
              )}
            </Box>
          </MenuItem>
        ))}
      </Menu>

      {/* Custom Date Range Picker Modal */}
      <CustomDateRangePicker
        isOpen={showCustomPicker}
        onClose={() => setShowCustomPicker(false)}
        onApply={handleCustomDateApply}
        initialStartDate={customStartDate}
        initialEndDate={customEndDate}
      />
    </>
  );
};

export default DateFilterDropdown;
